import React, { useState, useCallback } from "react";
import "moment/locale/pt-br";
import { useSelector } from "react-redux";

import { ModuleHeader } from '~/components/ModuleHeader';
import { Icon, Radio, Dropdown, Menu, Popover, Badge, Button } from "antd";
import Import from "./Import";
import InstagramConnect from "~/_domains/socialSelling/Header/InstagramConnect";
import DateNavigation from '~/_shared/components/DateNavigation';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import {
  formatProspectingDate,
  isProspectingPeriodDefault,
  countActiveProspectingFilters,
} from '../filters';

var DISPLAY_MODE_MAP = {
  funnel: { label: 'Funil', color: '#722ed1', icon: 'funnel-plot' },
  table: { label: 'Tabela', color: '#1890ff', icon: 'table' },
};

var truncateString = function(str, maxLength) {
  if (maxLength === undefined) maxLength = 25;
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
};

export var Header = function(props) {
  var showDrawer = props.showDrawer;
  var displayMode = props.displayMode;
  var setDisplayMode = props.setDisplayMode;
  var searchQuery = props.searchQuery;
  var setSearchQuery = props.setSearchQuery;
  var date = props.date;
  var setDate = props.setDate;
  var dateMode = props.dateMode;
  var setDateMode = props.setDateMode;
  var selectedCadence = props.selectedCadence;
  var setSelectedCadence = props.setSelectedCadence;
  var defaultFilters = props.defaultFilters || { date: date, dateMode: dateMode, search: '' };
  var onClearFilters = props.onClearFilters;

  var importModalState = useState(false);
  var isImportModalVisible = importModalState[0];
  var setIsImportModalVisible = importModalState[1];

  var allCadences = useSelector(function(state) { return state.settings['prospecting.cadence']; });

  var parsedCadences = React.useMemo(function() {
    if (!allCadences) return [];
    return allCadences.map(function(cadence) {
      try {
        var parsedValue = JSON.parse(cadence.value);
        return {
          id: cadence.id,
          name: parsedValue.name,
          data: parsedValue.data
        };
      } catch (e) {
        console.error('Error parsing cadence:', e);
        return null;
      }
    }).filter(Boolean);
  }, [allCadences]);

  var selectedCadenceName = React.useMemo(function() {
    var cadence = parsedCadences.find(function(c) { return c.id === selectedCadence; });
    return cadence ? truncateString(cadence.name) : null;
  }, [parsedCadences, selectedCadence]);

  var handleClearCadence = useCallback(function() {
    setSelectedCadence(null);
  }, [setSelectedCadence]);

  // When no cadence is selected, show minimal header
  var showCadenceControls = selectedCadence !== null;

  var handleMenuClick = useCallback(function(e) {
    if (e.key === 'manual') {
      showDrawer({});
    } else if (e.key === 'import') {
      setIsImportModalVisible(true);
    }
  }, [showDrawer]);

  var handleCloseImport = useCallback(function() {
    setIsImportModalVisible(false);
  }, []);

  var newLeadMenu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="manual">
        <Icon type="edit" style={{ marginRight: 8 }} />
        Cadastro manual
      </Menu.Item>
      <Menu.Item key="import">
        <Icon type="upload" style={{ marginRight: 8 }} />
        Importar em lote
      </Menu.Item>
    </Menu>
  );

  // Applied filters (period + search); the display mode is a view, not a filter.
  var appliedFilters = { date: date, dateMode: dateMode, search: searchQuery || '' };
  var isPeriodDefault = isProspectingPeriodDefault(appliedFilters, defaultFilters);

  // Count active filters: only what differs from the defaults
  var filterCount = showCadenceControls ? countActiveProspectingFilters(appliedFilters, defaultFilters) : 0;

  // Build pills
  var pills = [];
  if (showCadenceControls) {
    var modeInfo = DISPLAY_MODE_MAP[displayMode] || DISPLAY_MODE_MAP.funnel;
    pills.push({ key: 'mode', label: modeInfo.label, color: modeInfo.color, icon: modeInfo.icon });
    if (date) {
      pills.push({
        key: 'date',
        label: formatProspectingDate(date, dateMode),
        color: '#1890ff',
        icon: 'calendar',
        onRemove: isPeriodDefault ? undefined : function() {
          setDateMode(defaultFilters.dateMode);
          setDate(defaultFilters.date);
        },
      });
    }
    if (searchQuery) {
      pills.push({
        key: 'search',
        label: 'Busca: ' + searchQuery,
        color: '#722ed1',
        icon: 'search',
        onRemove: function() { setSearchQuery(''); },
      });
    }
  }

  var handleClearAllFilters = function() {
    if (onClearFilters) onClearFilters();
  };

  return (
    <>
      <Import
        visible={isImportModalVisible}
        onCancel={handleCloseImport}
      />
      <ModuleHeader
        breadcrumbs={showCadenceControls && selectedCadenceName
          ? ['CRM', { label: 'Prospecção Ativa', onClick: handleClearCadence }, selectedCadenceName]
          : ['CRM', 'Prospecção Ativa']
        }
        search={showCadenceControls ? (
          <Search
            value={searchQuery}
            onChange={function(e) { setSearchQuery(e.target.value); }}
            placeholder="Buscar leads"
          />
        ) : null}
        title={<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span>{showCadenceControls && selectedCadenceName ? selectedCadenceName : 'Prospecção Ativa'}</span>
        </div>}
        center={showCadenceControls ? (
          <>
          <Popover
            trigger="click"
            placement="bottomRight"
            content={
              <div style={{ width: 360 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {filterCount > 0 && (
                      <a
                        style={{ fontSize: 12, color: '#f5222d' }}
                        onClick={handleClearAllFilters}
                      >
                        Limpar tudo
                      </a>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Display mode */}
                  <div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visualização</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[
                        { value: 'funnel', label: 'Funil', color: '#722ed1', icon: 'funnel-plot' },
                        { value: 'table', label: 'Tabela', color: '#1890ff', icon: 'table' },
                      ].map(function(opt) {
                        var isActive = displayMode === opt.value;
                        return (
                          <div
                            key={opt.value}
                            onClick={function() { setDisplayMode(opt.value); }}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                              border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                              background: isActive ? opt.color + '0a' : '#fafafa',
                              transition: 'all 0.2s',
                            }}
                          >
                            <span style={{ fontSize: 12, color: isActive ? '#262626' : '#8c8c8c' }}>
                              <Icon type={opt.icon} style={{ marginRight: 4 }} />
                              {opt.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Date navigation */}
                  <div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Período</div>
                    <DateNavigation
                      date={date}
                      onChange={setDate}
                      mode={dateMode}
                      onModeChange={setDateMode}
                    />
                  </div>
                </div>
              </div>
            }
          >
            <Badge
              count={filterCount}
              offset={[-4, 0]}
              style={{ backgroundColor: '#722ed1' }}
            >
              <Button
                icon="bars"
                style={{ borderRadius: 8 }}
              >
                Filtros
              </Button>
            </Badge>
          </Popover>
          <InstagramConnect />
          <Dropdown overlay={newLeadMenu} trigger={['click']}>
            <Button icon="plus" shape="circle" />
          </Dropdown>
          </>
        ) : null}
        advanced={showCadenceControls && pills.length > 0 ? (
          React.createElement(FilterPillsBar, {
            pills: pills,
            onClearAll: filterCount > 0 ? handleClearAllFilters : undefined,
          })
        ) : null}
      />
    </>
  );
};